import { HTMLAttributes, FC } from "react";
import clsx from "classnames";
import {
  TypographyVariant,
  TypographyColor,
} from "../../../constants/typography";
import { Typography } from "../../core/Typography/Typography";
import { StaticImage } from "../../core/StaticImage/StaticImage";
import { useStyles } from "./ConsoleActivityCard.style";
import Clock from "../../../assets/console/clock.webp";
import Clock2x from "../../../assets/console/clock@2x.webp";
import Clock3x from "../../../assets/console/clock@3x.webp";
import CupColor from "../../../assets/console/cup-color.webp";
import CupColor2x from "../../../assets/console/cup-color@2x.webp";
import CupColor3x from "../../../assets/console/cup-color@3x.webp";
import { useAppSelector } from "../../hooks";
import { selectIsDesktopView } from "../../../store/slices/appState";
import { AnalyticsGA4 } from "../../../models/analyticsGA4";
import { useTranslation } from "react-i18next";

interface IConsoleServiceCardProps {
  totalTimePlayed: number;
  totalPlayedGames: number;
}

export const ConsoleActivityCard: FC<
  HTMLAttributes<HTMLDivElement> & IConsoleServiceCardProps
> = (props) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { totalPlayedGames, totalTimePlayed, className, ...otherProps } = props;
  const isDesktop = useAppSelector(selectIsDesktopView);

  const onClick = () => {
    AnalyticsGA4.homepageAction("activity");
  };

  if (!isDesktop) {
    return (
      <div
        {...otherProps}
        className={clsx(classes.rootMobile, className)}
        onClick={onClick}
      >
        <div className={classes.item}>
          <div className={classes.icon}>
            <StaticImage
              src={CupColor}
              src2x={CupColor2x}
              src3x={CupColor3x}
              className={classes.image}
            />
          </div>
          <div>
            <Typography
              variant={TypographyVariant.BodyB6Medium}
              color={TypographyColor.Silver}
              className={classes.mobileLabel}
            >
              {t("Console_Sessions Played")}
            </Typography>
            <Typography variant={TypographyVariant.BodyB5Bold}>
              {totalPlayedGames}
            </Typography>
          </div>
        </div>
        <div className={classes.item}>
          <div className={classes.icon}>
            <StaticImage
              src={Clock}
              src2x={Clock2x}
              src3x={Clock3x}
              className={classes.image}
            />
          </div>
          <div>
            <Typography
              variant={TypographyVariant.BodyB6Medium}
              color={TypographyColor.Silver}
              className={classes.mobileLabel}
            >
              {t("Console_Play Time")}
            </Typography>
            <Typography
              variant={TypographyVariant.BodyB5Bold}
            >{`${totalTimePlayed}h`}</Typography>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      {...otherProps}
      className={clsx(classes.root, className)}
      data-gamepad="activity-card"
      data-selected-class={classes.selected}
      data-button-sound="none"
      onClick={onClick}
    >
      <Typography variant={TypographyVariant.HeadingH5Bold}>
        {t("Console_My Activity")}
      </Typography>
      <div className={classes.detail}>
        <div className={classes.statInfo}>
          <StaticImage
            src={CupColor}
            src2x={CupColor2x}
            src3x={CupColor3x}
            className={classes.iconStat}
          />
          <div>
            <Typography
              variant={TypographyVariant.BodyB6Medium}
              className={classes.label}
            >
              {t("Console_Sessions Played")}
            </Typography>
            <Typography variant={TypographyVariant.HeadingH4Bold}>
              {totalPlayedGames}
            </Typography>
          </div>
        </div>
        <div className={classes.statInfo}>
          <StaticImage
            src={Clock}
            src2x={Clock2x}
            src3x={Clock3x}
            className={classes.iconStat}
          />
          <div>
            <Typography
              variant={TypographyVariant.BodyB6Medium}
              className={classes.label}
            >
              {t("Console_Play Time")}
            </Typography>
            <Typography variant={TypographyVariant.HeadingH4Bold}>
              {`${totalTimePlayed}h`}
            </Typography>
          </div>
        </div>
      </div>
    </div>
  );
};
